import { ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Select, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonIcons from "../../../../assets/icons";

import { useTranslation } from "react-i18next";
import { RootState } from "../../../../store/store";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import { getOrderAsync } from "../../../../store/reducers/orderSlice";

// ===== TYPES =====
interface OrderItem
{
   id: number;
   name: string;
   reason: string;
   created_at: number;

   priority: {
      value: "LOW" | "MEDIUM" | "HIGH";
      description?: string;
      name: string;
   };

   status: {
      name: string; // ORDER_CREATED, ORDER_PROCESSING...
      description?: string; // màu
      value: string;
   };

   order_details?: {
      provider?: {
         name?: string;
      };
   }[];
}

export default function ArchiveOrderListPage()
{
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const [searchParams, setSearchParams] = useSearchParams();

   const [data, setData] = React.useState<OrderItem[]>([]);
   const [total, setTotal] = React.useState(0);

   // Initialize state from URL query parameters
   const getInitialFilterValues = () =>
   {
      const pageParam = searchParams.get("page");
      const sizeParam = searchParams.get("size");
      const sortParam = searchParams.get("sort");
      const providerParam = searchParams.get("provider");
      const statusParam = searchParams.get("status");
      const startDateParam = searchParams.get("startDate");
      const endDateParam = searchParams.get("endDate");

      return {
         page: pageParam ? parseInt(pageParam) : 0,
         pageSize: sizeParam ? parseInt(sizeParam) : 10,
         sort: (sortParam as "asc" | "desc") || "desc",
         providerId: providerParam || "all",
         statusId: statusParam || "all",
         startDate: startDateParam ? parseInt(startDateParam) : null,
         endDate: endDateParam ? parseInt(endDateParam) : null,
      };
   };

   const initialFilters = getInitialFilterValues();

   const [page, setPage] = React.useState(initialFilters.page);
   const [pageSize, setPageSize] = React.useState(initialFilters.pageSize);
   const [sort, setSort] = React.useState<"asc" | "desc">(initialFilters.sort);
   const [providerId, setProviderId] = React.useState<string>(initialFilters.providerId);
   const [statusId, setStatusId] = React.useState<string>(initialFilters.statusId);
   const [startDate, setStartDate] = React.useState<number | null>(initialFilters.startDate);
   const [endDate, setEndDate] = React.useState<number | null>(initialFilters.endDate);

   const providers = useSelector(
      (state: RootState) => state.appCommonSlice.allProviders,
   );

   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   // Parse filters from URL search params
   const parseFiltersFromURL = (params: URLSearchParams) =>
   {
      const pageParam = params.get("page");
      const sizeParam = params.get("size");
      const sortParam = params.get("sort");
      const providerParam = params.get("provider");
      const statusParam = params.get("status");
      const startDateParam = params.get("startDate");
      const endDateParam = params.get("endDate");

      return {
         page: pageParam && !isNaN(parseInt(pageParam)) ? parseInt(pageParam) : 0,
         pageSize: sizeParam && !isNaN(parseInt(sizeParam)) ? parseInt(sizeParam) : 10,
         sort: (sortParam as "asc" | "desc") || "desc",
         providerId: providerParam || "all",
         statusId: statusParam || "all",
         startDate: startDateParam && !isNaN(parseInt(startDateParam)) ? parseInt(startDateParam) : null,
         endDate: endDateParam && !isNaN(parseInt(endDateParam)) ? parseInt(endDateParam) : null,
      };
   };

   // Update URL when filters change (skip initial mount)
   const isInitialMount = useRef(true);

   useEffect(() =>
   {
      if (isInitialMount.current)
      {
         isInitialMount.current = false;
         return;
      }

      const params = new URLSearchParams();

      if (page > 0) params.set("page", page.toString());
      if (pageSize !== 10) params.set("size", pageSize.toString());
      if (sort !== "desc") params.set("sort", sort);
      if (providerId !== "all") params.set("provider", providerId);
      if (statusId !== "all") params.set("status", statusId);
      if (startDate) params.set("startDate", startDate.toString());
      if (endDate) params.set("endDate", endDate.toString());

      setSearchParams(params, { replace: true });
   }, [page, pageSize, sort, providerId, statusId, startDate, endDate]);

   // Sync state when URL query parameters change (e.g., browser back/forward)
   useEffect(() =>
   {
      const filters = parseFiltersFromURL(searchParams);

      setPage(filters.page);
      setPageSize(filters.pageSize);
      setSort(filters.sort);
      setProviderId(filters.providerId);
      setStatusId(filters.statusId);
      setStartDate(filters.startDate);
      setEndDate(filters.endDate);
   }, [searchParams]);

   // Reset all filters to default values
   const handleResetFilters = () =>
   {
      setPage(0);
      setPageSize(10);
      setSort("desc");
      setProviderId("all");
      setStatusId("all");
      setStartDate(null);
      setEndDate(null);
   };

   const getData = async (forcePage?: number) =>
   {
      try
      {

         const currentPage = forcePage !== undefined ? forcePage : page;

         // @ts-ignore
         let res = await dispatch(
            // @ts-ignore
            getOrderAsync({
               order_type: "archive",
               page: currentPage,
               size: pageSize,
               sort: sort,
               provider_id: providerId === "all" ? undefined : providerId,
               status_id: statusId === "all" ? undefined : statusId,
               start_date: startDate ? Math.floor(startDate / 1000) : undefined,
               end_date: endDate ? Math.floor(endDate / 1000) : undefined,
            }),
         ).unwrap();


         setData(res?.data?.data || []);
         setTotal(res?.data?.pagination?.total || 0);
      } catch (error)
      {
         setData([]);
      }
   };

   // Sync state when URL query parameters change (e.g., browser back/forward)
   useEffect(() =>
   {
      const params = new URLSearchParams(searchParams);

      const pageParam = params.get("page");
      const sizeParam = params.get("size");
      const sortParam = params.get("sort");
      const providerParam = params.get("provider");
      const statusParam = params.get("status");
      const startDateParam = params.get("startDate");
      const endDateParam = params.get("endDate");

      setPage(pageParam ? parseInt(pageParam) : 0);
      setPageSize(sizeParam ? parseInt(sizeParam) : 10);
      setSort((sortParam as "asc" | "desc") || "desc");
      setProviderId(providerParam || "all");
      setStatusId(statusParam || "all");
      setStartDate(startDateParam ? parseInt(startDateParam) : null);
      setEndDate(endDateParam ? parseInt(endDateParam) : null);
   }, [searchParams]);

   const columns: ColumnsType<OrderItem> = [{
      title: t("No."),
      key: "index",
      width: 70,
      render: (_: any, __: any, index: number) => page * pageSize + index + 1,
      align: "center",
   },
   {
      title: t("Order Name"),
      dataIndex: "name",
      key: "name",
   },
   {
      title: t("Provider"),
      key: "provider",
      render: (item: OrderItem) =>
      {
         if (!item?.order_details?.length) return <span>N/A</span>;

         // Get unique provider names from all order_details
         const providerNames = item.order_details
            .map(detail => detail?.provider?.name)
            .filter((name, index, self) => name && self.indexOf(name) === index);

         return <span>{providerNames.join(", ") || "N/A"}</span>;
      },
      width: 250,
   },
   {
      title: t("Total images"),
      key: "total_images",
      render: (item: OrderItem) =>
      {
         // Calculate total images from all order_details
         const total = item?.order_details?.reduce((sum, detail: any) =>
         {
            return sum + (detail?.images?.length || 0);
         }, 0) || 0;
         return <span>{total}</span>;
      },
      width: 150,
   },
   {
      title: t("Ordered at"),
      dataIndex: "created_at",
      key: "created_at",
      render: (timestamp: number) =>
         dayjs(timestamp).format("DD/MM/YYYY HH:mm"),
      width: 250,
   },
   // {
   //    title: t("Reason"),
   //    dataIndex: "reason",
   //    key: "reason",
   // },
   {
      title: t("Status"),
      key: "status",
      render: (item: OrderItem) => (
         <Tag
            color={item?.status?.description}
            className="px-3 py-1 rounded-full"
         >
            {t(item?.status?.value)}
         </Tag>
      ),
      width: 200,
   },
   {
      title: t("Priority"),
      key: "priority",
      render: (item: OrderItem) => (
         <p style={{ color: item?.priority?.description }}>
            {item?.priority?.name}
         </p>
      ),
      width: 150,
   },
   {
      title: "",
      key: "action",
      align: "right",
      render: (_: any, record: any) => (
         <div className="flex items-center text-center  justify-end">
            <Button type="link" onClick={() => navigate(`./${record.id}`)}>
               {t("Detail")}
            </Button>

            <Button
               className="!w-6 min-w-6 h-6"
               type="text"
               icon={<CommonIcons.NextIcon />}
               onClick={() => navigate(`./${record.id}`)}
            />
         </div>
      ),
      width: 200,
   },
   ];

   useEffect(() =>
   {
      getData();
   }, [page, pageSize, providerId, statusId, sort, startDate, endDate]);


   return (
      <ContentLayoutManagement
         header={<HeaderContent breadcrumb={[{ title: t("Archive Orders") }]} />}
      >
         <div className="p-6 bg-[#0f172a] h-full overflow-hidden text-white">
            {/* Filters */}
            <div className="flex gap-3 mb-4 w-full">
               <div className="w-full">
                  {" "}
                  <p className="mb-1" style={{ fontSize: "14px" }}>
                     {t("Created at")}
                  </p>
                  <div className="flex gap-3">
                     <DatePicker
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(date, dateString) =>
                           setStartDate(date ? date.valueOf() : null)
                        }
                        placeholder={t("Start date")}
                        className=" div-dropdown"
                        format='DD/MM/YYYY'
                     />
                     <DatePicker
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(date, dateString) =>
                           setEndDate(date ? date.valueOf() : null)
                        }
                        placeholder={t("End date")}
                        className=" div-dropdown"
                        format='DD/MM/YYYY'
                     />
                  </div>
               </div>

               <div className="w-full">
                  <p style={{ fontSize: "14px" }} className="mb-1">
                     {t("Provider")}
                  </p>
                  <Select
                     value={providerId}
                     onChange={(val) => setProviderId(val)}
                     className="div-dropdown"
                     options={[{ value: "all", label: t("All") }].concat(
                        providers?.map((provider?: any) => ({
                           value: provider.id.toString(),
                           label: provider.name,
                        })) || [],
                     )}
                  />
               </div>

               <div className="w-full">
                  <p className="mb-1" style={{ fontSize: "14px" }}>
                     {t("Status")}
                  </p>
                  <Select
                     value={statusId}
                     onChange={(val) => setStatusId(val)}
                     className=" div-dropdown"
                     options={[{ value: "all", label: t("All") }].concat(
                        dataStatusOrder
                           ?.map((item: any) => ({
                              value: item.id.toString(),
                              label: t(item.name),
                           })) || [],
                     )}
                  />
               </div>

               <div className="min-w-max">
                  <p style={{ fontSize: "14px" }} className="mb-1">
                     {t("Sort")}
                  </p>
                  <div className="flex gap-3">
                     <div className="!bg-[var(--neutral-900)] h-9 rounded-md p-1 flex gap-2 cursor-pointer min-w-max">
                        <div
                           className="flex gap-1 justify-center items-center rounded p-1"
                           style={{
                              background: sort === "asc" ? "#5370A4" : "",
                              fontSize: "14px",
                           }}
                           onClick={() => setSort("asc")}
                        >
                           <CommonIcons.AscendingIcon />
                           {t("Ascending")}
                        </div>
                        <div
                           className="flex gap-1 justify-center items-center rounded p-1"
                           style={{
                              background: sort === "desc" ? "#5370A4" : "",
                              fontSize: "14px",
                           }}
                           onClick={() => setSort("desc")}
                        >
                           <CommonIcons.DecreasingIcon />
                           {t("Descending")}
                        </div>
                     </div>

                     <Button
                        className="!bg-[var(--neutral-900)]  !border-none !w-9 !h-9 !min-w-9 !min-h-9"
                        icon={<ReloadOutlined />}
                        onClick={handleResetFilters}
                     />
                  </div>
               </div>
            </div>

            {/* Table */}

            <Table
               columns={columns}
               dataSource={data}
               pagination={{
                  current: page + 1, // Ant Design uses 1-indexed pages
                  pageSize: pageSize,
                  total: total,
                  showTotal: (total, range) =>
                     `${t("Show")} ${range[0]} to ${range[1]} of ${total} ${t("records")}`,
                  onChange: (page: number, pageSize: number) =>
                  {
                     setPage(page - 1); // Convert back to 0-indexed for API
                     setPageSize(pageSize);
                  },
                  pageSizeOptions: ['10', '20', '50', '100'],
               }}
            />
         </div>
      </ContentLayoutManagement>
   );
}
